import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = newsList.find((n) => n.id === id);

  usePageMeta({
    title: item ? `${item.title} - 影视资讯 - 聚会的目的在线` : "资讯不存在 - 聚会的目的在线",
    description: item ? item.summary : "抱歉，你访问的资讯内容不存在，请返回资讯页浏览更多内容。",
    keywords: item ? `${item.category},影视资讯,聚会的目的在线` : "聚会的目的在线,影视资讯",
  });

  if (!item) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-8 py-16 md:py-24">
          <EmptyState
            icon="ri-alert-line"
            title="该资讯内容不存在"
            description="你访问的资讯可能已下线，或链接有误。返回资讯列表查看更多影视动态。"
            actionTo="/news"
          />
        </div>
      </Layout>
    );
  }

  const related = newsList.filter((n) => n.id !== item.id && n.category === item.category).slice(0, 3);
  const fallbackRelated = newsList.filter((n) => n.id !== item.id).slice(0, 3);
  const list = related.length > 0 ? related : fallbackRelated;

  return (
    <Layout>
      <article className="w-full px-4 md:px-8 py-8 md:py-10">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-600 cursor-pointer transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/news" className="hover:text-primary-600 cursor-pointer transition-colors">
            影视资讯
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800 line-clamp-1">{item.category}</span>
        </nav>

        <div className="max-w-3xl">
          <span className="inline-block px-3 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium mb-4">
            {item.category}
          </span>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-tight">
            {item.title}
          </h1>
          <div className="mt-4 flex items-center gap-3 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {item.date}
            </span>
            <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
            <span>聚会的目的在线 · 资讯中心</span>
          </div>

          <div className="mt-6 rounded-lg overflow-hidden border border-background-200">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} 资讯配图`}
              className="w-full aspect-[16/9] object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {item.content.map((para) => (
              <p key={para.slice(0, 16)} className="text-sm md:text-base text-foreground-700 leading-loose">
                {para}
              </p>
            ))}
          </div>

          <div className="mt-10 p-5 rounded-lg bg-background-100 border border-background-200">
            <p className="text-xs text-foreground-500 leading-relaxed">
              本文为演示内容，由聚会的目的在线资讯中心整理，仅用于展示平台资讯阅读功能，不构成任何真实报道。
            </p>
          </div>
        </div>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading font-semibold text-xl text-foreground-950 mb-5 flex items-center gap-2">
            <span className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center">
              <i className="ri-links-line"></i>
            </span>
            相关阅读
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {list.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className="group block rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-full aspect-[16/9] overflow-hidden bg-background-200">
                  <img
                    src={n.cover}
                    alt={n.title}
                    title={`${n.title} 资讯配图`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                    {n.title}
                  </h3>
                  <p className="mt-2 text-xs text-foreground-400">{n.date}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </article>
    </Layout>
  );
}